import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Mail, Lock, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { registerUser } from "@/utils/helper/auth";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { generatePassword } from "@/lib/password-utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function SignupModal({ isOpen, onOpenChange, onSwitchToLogin }) {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone: value }));
  };

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const getStrengthText = (strength) => {
    if (strength === 0) return "";
    if (strength <= 25) return "Weak";
    if (strength <= 50) return "Fair";
    if (strength <= 75) return "Good";
    return "Strong";
  };

  const getStrengthColor = (strength) => {
    if (strength <= 25) return "bg-red-500";
    if (strength <= 50) return "bg-yellow-500";
    if (strength <= 75) return "bg-blue-500";
    return "bg-green-500";
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword(16);
    setFormData((prev) => ({
      ...prev,
      password: newPassword,
      confirmPassword: newPassword,
    }));
    toast.success("Secure password generated!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      return toast.error("You must accept the Terms and Privacy Policy");
    }

    const { confirmPassword, ...cleanedFormData } = formData;

    if (formData.password !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    setIsSubmitting(true);

    try {
      await registerUser(cleanedFormData);
      toast.success("Registration successful! Please check your email.");
      onOpenChange(false);
      // Reset form
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        referralCode: "",
      });
      setAcceptedTerms(false);
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage =
        error?.response?.data?.message || "Registration failed";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      referralCode: "",
    });
    setAcceptedTerms(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold text-primary">
            Create Account
          </DialogTitle>
          <DialogDescription className="text-center">
            Join us to save your cart and track orders
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="modal-firstName"
                className="text-primary font-medium"
              >
                First Name
              </Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="modal-firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="First name"
                  className="pl-10 py-3 border-gray-200 focus:border-secondary"
                  required
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="modal-lastName"
                className="text-primary font-medium"
              >
                Last Name
              </Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  id="modal-lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Last name"
                  className="pl-10 py-3 border-gray-200 focus:border-secondary"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="modal-email" className="text-primary font-medium">
              Email Address
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="modal-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className="pl-10 py-3 border-gray-200 focus:border-secondary"
                required
              />
            </div>
          </div>

          <div>
            <Label className="text-primary font-medium">Phone Number</Label>
            <div className="mt-2">
              <PhoneInput
                country={"ng"}
                value={formData.phone}
                onChange={handlePhoneChange}
                inputClass="!w-full !py-3 !pl-14 !border-gray-200 !focus:border-secondary"
                containerClass="!w-full"
                buttonClass="!border-gray-200 !bg-gray-50"
                required
              />
            </div>
          </div>

          <div>
            <Label
              htmlFor="modal-password"
              className="text-primary font-medium"
            >
              Password
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="modal-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                className="pl-10 pr-20 py-3 border-gray-200 focus:border-secondary"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary hover:text-secondary/80 text-xs font-medium"
              >
                Generate
              </button>
            </div>

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Password strength:</span>
                  <span
                    className={`font-medium ${
                      passwordStrength <= 25
                        ? "text-red-500"
                        : passwordStrength <= 50
                          ? "text-yellow-500"
                          : passwordStrength <= 75
                            ? "text-blue-500"
                            : "text-green-500"
                    }`}
                  >
                    {getStrengthText(passwordStrength)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getStrengthColor(passwordStrength)}`}
                    style={{ width: `${passwordStrength}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label
              htmlFor="modal-confirmPassword"
              className="text-primary font-medium"
            >
              Confirm Password
            </Label>
            <div className="relative mt-2">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="modal-confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                className="pl-10 pr-10 py-3 border-gray-200 focus:border-secondary"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <Label
              htmlFor="modal-referralCode"
              className="text-primary font-medium"
            >
              Referral Code (Optional)
            </Label>
            <div className="relative mt-2">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="modal-referralCode"
                name="referralCode"
                type="text"
                value={formData.referralCode}
                onChange={handleChange}
                placeholder="Enter referral code if you have one"
                className="pl-10 py-3 border-gray-200 focus:border-secondary"
              />
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="modal-terms"
              checked={acceptedTerms}
              onCheckedChange={setAcceptedTerms}
            />
            <Label
              htmlFor="modal-terms"
              className="text-sm text-gray-600 leading-relaxed"
            >
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                className="text-secondary hover:text-secondary/80 underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                className="text-secondary hover:text-secondary/80 underline"
              >
                Privacy Policy
              </a>
            </Label>
          </div>

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin h-5 w-5" />
                Creating Account...
              </span>
            ) : (
              "Create Account"
            )}
          </Button>
        </form>

        <DialogFooter className="flex-col space-y-2">
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onSwitchToLogin();
                }}
                className="text-secondary hover:text-secondary/80 font-medium"
              >
                Sign in here
              </button>
            </p>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
